import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadCloud, FileText, X, Loader2, ShieldCheck } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function Dashboard() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) validate(f);
  }, []);

  const validate = (f) => {
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported.");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File exceeds 15 MB limit.");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setLoading(true); setProgress(8);
    const form = new FormData();
    form.append("file", file);

    // simulated progress while waiting for AI
    const tick = setInterval(() => setProgress((p) => Math.min(p + 4, 90)), 500);
    try {
      const { data } = await api.post("/cos/check", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      clearInterval(tick); setProgress(100);
      toast.success("Analysis complete");
      navigate(`/app/result/${data.id}`);
    } catch (err) {
      clearInterval(tick);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">New check</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">
              Upload a Certificate of Sponsorship.
            </h1>
            <p className="text-zinc-600 mt-3">
              Drop the CoS PDF as issued by UKVI. We inspect the metadata, run heuristics,
              and cross-check with an AI expert reviewer.
            </p>

            <div className="mt-6 border border-zinc-200 bg-white rounded-sm p-4 flex flex-wrap items-center justify-between gap-3" data-testid="dashboard-pricing-hint">
              <div className="flex items-center gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">This automated check</div>
                  <div className="font-heading text-xl font-bold tracking-tight">Free</div>
                </div>
                <div className="h-8 w-px bg-zinc-200" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Add human expert review</div>
                  <div className="font-heading text-xl font-bold tracking-tight">£100 <span className="font-mono text-xs text-zinc-500">GBP</span></div>
                </div>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 max-w-xs text-right">
                Upgrade to a human verdict any time after your free result
              </div>
            </div>

            <div
              className="dashed-drop mt-8 rounded-sm bg-white p-10 grid place-items-center min-h-[300px]"
              data-active={dragActive}
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              data-testid="upload-dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && validate(e.target.files[0])}
                data-testid="upload-file-input"
              />
              {!file ? (
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 grid place-items-center bg-zinc-100 rounded-sm">
                    <UploadCloud className="w-6 h-6 text-zinc-700" />
                  </div>
                  <div className="mt-4 font-heading text-xl font-semibold">
                    Drop your CoS PDF here
                  </div>
                  <div className="text-sm text-zinc-500 mt-1">or click to browse. Max 15MB.</div>
                  <Button className="mt-6 rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90" onClick={() => inputRef.current?.click()} data-testid="upload-browse-btn">
                    <FileText className="w-4 h-4" /> Browse files
                  </Button>
                </div>
              ) : (
                <div className="w-full max-w-md text-center">
                  <div className="border border-zinc-300 rounded-sm p-4 flex items-center justify-between gap-3 bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-[#0F172A]" />
                      <div className="min-w-0 text-left">
                        <div className="font-mono text-sm truncate" data-testid="upload-filename">{file.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                          {(file.size / 1024).toFixed(1)} KB · PDF
                        </div>
                      </div>
                    </div>
                    {!loading && (
                      <button className="text-zinc-500 hover:text-zinc-900" onClick={() => setFile(null)} data-testid="upload-clear-btn">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {loading ? (
                    <div className="mt-6">
                      <div className="flex items-center gap-2 justify-center text-sm text-zinc-700">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Extracting metadata & running expert review…
                      </div>
                      <Progress value={progress} className="mt-3 h-1.5 rounded-none" />
                    </div>
                  ) : (
                    <Button onClick={submit} className="mt-6 rounded-sm w-full bg-[#0F172A] hover:bg-[#0F172A]/90 h-11" data-testid="upload-analyze-btn">
                      <ShieldCheck className="w-4 h-4" /> Analyse this certificate
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-4">
            <div className="border border-zinc-200 bg-white rounded-sm p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">What we check</div>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  "PDF producer & creator fingerprint",
                  "Author, subject, keywords, XMP data",
                  "Creation vs. modification timestamps",
                  "Digital signature / AcroForm signatures",
                  "'Certificate of Sponsorship' text markers",
                  "'Tier and Category' section detection",
                  "AI expert reasoning (Claude Sonnet 4.5)",
                ].map((t, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-mono text-[10px] mt-1 text-zinc-500">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-zinc-700">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-zinc-200 bg-[#0F172A] text-white rounded-sm p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Privacy</div>
              <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                Your PDF is processed in-memory. We store the metadata report — not your original document.
              </p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
